import { Select } from "../ui/select.tsx";
import { useQuery } from "@tanstack/react-query";
import type * as C from "@zooi/contracts";
import { type FormEvent, useEffect, useState } from "react";
import { ApiError, oauthApi } from "../api.ts";
import { CONSENT_LABELS as L } from "../labels.ts";
import { list } from "../format.ts";
import { AuthFrame } from "../auth-frame.tsx";
import { Button, Callout, ErrorText, Kbd } from "../ui.tsx";
import { PropertyList, PropertyRow } from "../ui/props.tsx";
import { Skeleton } from "../ui/skeleton.tsx";

const DURATIONS = [7, 30, 90] as const;

/**
 * The OAuth consent page (plan M1, step 4; docs/design-system.md §0.5): a
 * single-purpose confirmation in the person's session. Everything the
 * decision rests on is shown in full: who asks (in its own words, marked as
 * such), where the browser returns, what it asks for. The person chooses the
 * products, one zooi access level and how long the connection lasts.
 */
export function OAuthConsent() {
  const query = window.location.search.slice(1);
  const view = useQuery({ queryKey: ["oauth-consent", query], queryFn: () => oauthApi.consent(query), retry: false, staleTime: Infinity });
  const signedOut = view.error instanceof ApiError && view.error.status === 401;
  useEffect(() => {
    // Without a session, sign in first; the signed request rides along and the authorization resumes after.
    if (signedOut) window.location.replace(`/sign-in?${query}`);
  }, [signedOut, query]);

  if (view.isLoading || signedOut) return <Frame><Skeleton shape="text" /></Frame>;
  if (!view.data) return <Frame><h1>{L.invalid}</h1><p className="z-muted">{L.invalidNote}</p><ErrorText error={view.error} /></Frame>;
  return <ConsentForm query={query} view={view.data} />;
}

const Frame = AuthFrame;

function ConsentForm({ query, view }: { query: string; view: C.ConsentView }) {
  const offered = (["agent", "read"] as const).filter((scope) => view.requested_scopes.includes(`zooi:${scope}`));
  const [scope, setScope] = useState<"read" | "agent" | null>(offered[0] ?? null);
  const [productIds, setProductIds] = useState<string[]>(view.products.length === 1 ? [view.products[0].id] : []);
  const [days, setDays] = useState<number>(30);
  const [busy, setBusy] = useState<"allow" | "deny" | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [leaving, setLeaving] = useState(false);
  const decide = async (accept: boolean) => {
    setBusy(accept ? "allow" : "deny");
    setError(null);
    try {
      const out = await oauthApi.decide(accept ? { oauth_query: query, accept, product_ids: productIds, scopes: [scope!], expires_in_days: days } : { oauth_query: query, accept });
      setLeaving(true);
      window.location.assign(out.redirect_to);
    } catch (err) { setError(err); setBusy(null); }
  };
  const submit = (event: FormEvent) => { event.preventDefault(); if (scope && productIds.length) void decide(true); };
  if (leaving) return <Frame><p role="status">{L.returning}</p></Frame>;
  return (
    <Frame>
      <h1>{L.title(view.client.name)}</h1>
      <p className="z-muted">{L.intro}</p>
      <PropertyList>
        <PropertyRow label={L.returnsTo}><span className="z-mono">{view.client.redirect_hosts.join(", ")}</span></PropertyRow>
        <PropertyRow label={L.requested}>{list(view.requested_scopes.map((s) => L.scope[s] ?? s))}</PropertyRow>
      </PropertyList>
      {view.client.name && <p className="z-small z-muted">{L.nameNote}</p>}
      <form className="z-flow" onSubmit={submit}>
        {offered.length > 1 && (
          <div className="z-flow" role="radiogroup" aria-labelledby="consent-access">
            <h3 id="consent-access">{L.access}</h3>
            <ul className="z-check">
              {offered.map((option) => <li key={option}>
                <input id={`consent-scope-${option}`} type="radio" name="scope" value={option} checked={scope === option} disabled={busy !== null} onChange={() => setScope(option)} aria-describedby={`consent-scope-${option}-note`} />
                <div><label htmlFor={`consent-scope-${option}`} className="z-row-title">{option === "agent" ? L.agent : L.read}</label><span id={`consent-scope-${option}-note`} className="z-help">{option === "agent" ? L.agentNote : L.readNote}</span></div>
              </li>)}
            </ul>
          </div>
        )}
        {offered.length === 1 && <p className="z-small">{offered[0] === "agent" ? L.agentNote : L.readNote}</p>}
        <div className="z-flow" role="group" aria-labelledby="consent-products">
          <h3 id="consent-products">{L.products}</h3>
          <p className="z-small z-muted">{L.productsHelp}</p>
          {view.products.length ? <ul className="z-check">
            {view.products.map((product) => <li key={product.id}>
              <input id={`consent-product-${product.id}`} type="checkbox" checked={productIds.includes(product.id)} disabled={busy !== null} onChange={(event) => setProductIds((ids) => event.target.checked ? [...ids, product.id] : ids.filter((id) => id !== product.id))} />
              <label htmlFor={`consent-product-${product.id}`}>{product.name}</label>
            </li>)}
          </ul> : <p className="z-empty">{L.noProducts}</p>}
        </div>
        <div className="z-field">
          <label htmlFor="consent-duration">{L.duration}</label>
          <Select variant="form" id="consent-duration" label={L.duration} value={String(days)} disabled={busy !== null} onValueChange={(nextValue) => setDays(Number(nextValue))} options={DURATIONS.map((d) => ({ value: String(d), label: L.days(d) }))} />
        </div>
        <Callout>{L.governance}</Callout>
        <ErrorText error={error} />
        <div className="z-auth-actions">
          <Button busy={busy === "deny"} disabled={busy !== null} onClick={() => void decide(false)}>{L.deny}</Button>
          <Button tone="primary" type="submit" busy={busy === "allow"} disabled={!scope || !productIds.length || busy !== null}>{L.allow} <Kbd>Enter</Kbd></Button>
        </div>
      </form>
    </Frame>
  );
}
